import { useEffect, useState } from "react";
import { fromElement, selectorOf, type Target } from "../settings/cssSelector";

/**
 * DÉSIGNER UN ÉLÉMENT PRÉCIS SUR LA PAGE.
 *
 * ⚠️ IL A REMPLACÉ UNE LISTE DE BLOCS, et il fallait bien en venir là. La liste ne
 * proposait que les 29 blocs nommés, et les nommait par leur FAMILLE : les quatre
 * blocs du pied de page y portaient tous « Pied de page ». Elle ne permettait ni de
 * distinguer l'un de l'autre, ni d'atteindre un mot, un bouton, une case.
 *
 * ⚠️ LE SÉLECTEUR RESTE ANCRÉ sur le bloc nommé le plus proche — voir `selecteurDe`.
 * Viser précisément ne veut pas dire écrire un chemin depuis la racine : ce qui est
 * au-dessus du bloc n'a rien à voir avec ce qu'on style, et bouge au premier
 * remaniement.
 *
 * Displayed strings are French: the organiser is a French speaker.
 */

/** Ce que le viseur rend quand on a cliqué. */
export type Aim = Target & { element: HTMLElement };

export function Aimer({
  onAim,
  onCancel,
}: {
  onAim: (v: Aim) => void;
  onCancel: () => void;
}) {
  const [hovered, setHovered] = useState<HTMLElement | null>(null);
  const [box, setBox] = useState<DOMRect | null>(null);

  useEffect(() => {
    /* ⚠️ SUR `document`, EN CAPTURE, ET LE CLIC EST AVALÉ. Pendant qu'on vise, la page
       reste une page : ses liens naviguent, ses boutons se déclenchent, et « choisir le
       bouton Continuer » quitterait l'écran au lieu de le désigner. La capture prend
       l'événement avant lui, `preventDefault` l'annule. */
    const inside = (e: Event): HTMLElement | null => {
      const el = e.target;
      if (!(el instanceof HTMLElement)) return null;
      // Le panneau et sa carte ne se personnalisent pas : ce sont les outils.
      return el.closest(".site") ? el : null;
    };

    const hover = (e: MouseEvent) => {
      const el = inside(e);
      setHovered(el);
      setBox(el ? el.getBoundingClientRect() : null);
    };

    const click = (e: MouseEvent) => {
      const el = inside(e);
      if (!el) return;
      e.preventDefault();
      e.stopPropagation();
      const node = fromElement(el);
      if (node) onAim({ ...selectorOf(node), element: el });
    };

    const picked = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };

    document.addEventListener("mouseover", hover, true);
    document.addEventListener("click", click, true);
    document.addEventListener("keydown", picked, true);
    document.body.classList.add("vise");

    return () => {
      document.removeEventListener("mouseover", hover, true);
      document.removeEventListener("click", click, true);
      document.removeEventListener("keydown", picked, true);
      document.body.classList.remove("vise");
    };
  }, [onAim, onCancel]);

  /* La page défile sous le cadre, et la fenêtre se redimensionne. */
  useEffect(() => {
    if (!hovered) return;
    const follow = () => setBox(hovered.getBoundingClientRect());
    window.addEventListener("scroll", follow, true);
    window.addEventListener("resize", follow);
    return () => {
      window.removeEventListener("scroll", follow, true);
      window.removeEventListener("resize", follow);
    };
  }, [hovered]);

  return (
    <>
      {box && (
        <div
          className="vise__c"
          aria-hidden="true"
          style={{
            left: box.left,
            top: box.top,
            width: box.width,
            height: box.height,
          }}
        />
      )}
      <div className="vise__b" role="status">
        <b>Cliquez l'élément à personnaliser</b>
        <span>
          {hovered
            ? selectorOf(fromElement(hovered)!).selector
            : "Survolez la page"}
        </span>
        <button type="button" onClick={onCancel}>
          Annuler (Échap)
        </button>
      </div>
    </>
  );
}
